/**
 * Global middleware — runs on every client-side route navigation.
 * Sends a lightweight page view ping to the Nuxt server, which forwards
 * it to the Laravel backend for storage.
 *
 * Best practices:
 *  - Skips admin pages (don't pollute analytics with internal traffic)
 *  - Skips bots (server-side UA check done in the API)
 *  - Fire-and-forget: never blocks navigation
 *  - Debounced via sessionStorage to avoid double-counting within 2s
 */
export default defineNuxtRouteMiddleware((to) => {
  // Only run in the browser
  if (import.meta.server) return;

  // Skip admin and auth pages
  if (to.path.startsWith('/admin') || to.path.startsWith('/sanctum')) return;

  // Debounce: skip if same page was tracked in last 2 seconds
  const debounceKey = `pv_${to.path}`;
  const lastTracked = Number(sessionStorage.getItem(debounceKey) || 0);
  if (Date.now() - lastTracked < 2000) return;
  sessionStorage.setItem(debounceKey, String(Date.now()));

  // Fire-and-forget — never await, never block navigation
  navigator.sendBeacon
    ? navigator.sendBeacon('/analytics/track', new Blob([JSON.stringify({
        page: to.path,
        referrer: document.referrer || null,
        title: document.title || null,
      })], { type: 'application/json' }))
    : fetch('/analytics/track', {
        method: 'POST',
        body: JSON.stringify({ page: to.path, referrer: document.referrer || null, title: document.title || null }),
        headers: { 'Content-Type': 'application/json' },
        keepalive: true,
      }).catch(() => {}); // silent fail — analytics should never break the app
});
